import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MdMenu, MdLogout, MdNotifications, MdClose, MdSettings,
  MdPayments, MdCampaign, MdCheckCircle, MdCreditCard, MdEmojiEvents,
  MdCancel, MdNotificationsNone, MdSwapHoriz, MdLightMode, MdDarkMode,
} from 'react-icons/md';
import useAuth from '../../hooks/useAuth';
import useClubVocab from '../../hooks/useClubVocab';
import useTheme from '../../context/ThemeContext';
import { getNotifications, markRead, markAllRead } from '../../services/notificationService';
import { updateTheme } from '../../services/userService';

const PAGE_TITLES = {
  '/admin/dashboard':           'Dashboard',
  '/admin/students':            'Students',
  '/admin/trainers':            'Trainers',
  '/admin/classes':             'Classes',
  '/admin/payments':            'Payments',
  '/admin/belt-exams':          'Belt Exams',
  '/admin/announcements':       'Announcements',
  '/admin/reports':             'Reports',
  '/admin/attendance':          'Attendance',
  '/admin/settings':            'Settings',
  '/admin/approvals':           'Pending Approvals',
  '/admin/expenses':            'Expenses',
  '/admin/sessions':            'Sessions',
  '/admin/admins':              'Admins',
  '/admin/subscription':        'Subscription',
  '/admin/trial-students':      'Website Requests',
  '/admin/marketing':           'Marketing',
  '/admin/club-profile':        'Club Profile',
  '/admin/support-access':      'Support Access',
  '/admin/launch-testing':      'Launch Testing',
  '/admin/activity':            'Activity Log',
  '/trainer/dashboard':         'Dashboard',
  '/trainer/students':          'My Students',
  '/trainer/attendance':        'Mark Attendance',
  '/trainer/schedule':          'My Schedule',
  '/trainer/belt-recommendations': 'Belt Recommendations',
  '/trainer/announcements':     'Announcements',
  '/trainer/settings':          'Settings',
  '/trainer/sessions':          'Sessions',
  '/trainer/belt-exams':        'Belt Exams',
  '/trainer/expenses':          'Club Expenses',
  '/trainer/trial-students':    'Website Requests',
  '/student/dashboard':         'My Dashboard',
  '/student/profile':           'My Profile',
  '/student/attendance':        'My Attendance',
  '/student/schedule':          'Class Schedule',
  '/student/belt-progress':     'Belt Progress',
  '/student/fees':              'Fee Payment',
  '/student/notices':           'Notices',
  '/student/badges':            'My Badges',
  '/student/leaderboard':       'Leaderboard',
  '/student/settings':          'Settings',
};

const ROLE_BADGE = {
  admin:   'bg-nav-hover text-muted',
  trainer: 'bg-blue-100 text-blue-700',
  student: 'bg-green-100 text-green-700',
};

// Per-type icon + adaptive tint chip (bg-*-100/text-*-700 read in both themes).
const NOTIF_META = {
  fee_due:         { Icon: MdPayments,      tile: 'bg-amber-100 text-amber-700'   },
  announcement:    { Icon: MdCampaign,      tile: 'bg-blue-100 text-blue-700'     },
  attendance:      { Icon: MdCheckCircle,   tile: 'bg-green-100 text-green-700'   },
  subscription:    { Icon: MdCreditCard,    tile: 'bg-purple-100 text-purple-700' },
  exam_scheduled:  { Icon: MdEmojiEvents,   tile: 'bg-amber-100 text-amber-700'   },
  class_cancelled: { Icon: MdCancel,        tile: 'bg-red-100 text-red-600'       },
  class_placement: { Icon: MdSwapHoriz,     tile: 'bg-orange-100 text-orange-700' },
  general:         { Icon: MdNotifications, tile: 'bg-gray-100 text-muted'        },
};
const notifMeta = (type) => NOTIF_META[type] || NOTIF_META.general;

// Remap stale links saved on already-created notification rows to their real
// route, so old notifications don't hit the catch-all (→ /login) on click.
const LEGACY_LINK_FIX = {
  '/admin/monthly-fees': '/admin/payments',
};

// Resolve a notification's link to a safe, internal SPA path (or null to skip nav).
const resolveNotifLink = (link) => {
  const target = LEGACY_LINK_FIX[link] || link;
  // Only navigate to internal app paths; empty/external/malformed → no navigation.
  return target && target.startsWith('/') ? target : null;
};

const fmtRelative = (d) => {
  const diff = Date.now() - new Date(d).getTime();
  const min  = Math.floor(diff / 60000);
  if (min < 1)   return 'just now';
  if (min < 60)  return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr  < 24)  return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
};

function Navbar({ onMenuToggle }) {
  const { user, logout, updateUser } = useAuth();
  const { currentTheme, applyTheme } = useTheme();
  const navigate              = useNavigate();
  const { pathname }          = useLocation();
  const queryClient           = useQueryClient();
  const v                     = useClubVocab();
  const [notifOpen, setOpen]  = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const dropdownRef           = useRef(null);
  const profileRef            = useRef(null);
  const pageTitle             = (PAGE_TITLES[pathname]
    || (pathname.startsWith('/admin/classes/') ? 'Class Detail' : 'TrainAxis'))
    .replace(/\bStudents\b/g, v.members).replace(/\bStudent\b/g, v.member);

  // Fetch notifications — poll every 60 seconds
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn:  () => getNotifications().then(r => r.data),
    enabled:  !!user,
    refetchInterval: 60000,
  });

  const unreadCount   = data?.unreadCount || 0;
  const notifications = data?.notifications || [];

  const readMutation = useMutation({
    mutationFn: (id) => markRead(id),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const readAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Entrance flags — flip true one frame after open so the panel transitions in
  // (opacity/scale) instead of popping. Reset on close so the next open re-animates.
  const [notifShown,   setNotifShown]   = useState(false);
  const [profileShown, setProfileShown] = useState(false);
  useEffect(() => {
    if (!notifOpen) { setNotifShown(false); return; }
    const id = requestAnimationFrame(() => setNotifShown(true));
    return () => cancelAnimationFrame(id);
  }, [notifOpen]);
  useEffect(() => {
    if (!profileOpen) { setProfileShown(false); return; }
    const id = requestAnimationFrame(() => setProfileShown(true));
    return () => cancelAnimationFrame(id);
  }, [profileOpen]);

  // Close either dropdown on scroll — an anchored panel would otherwise float
  // mid-page once the navbar's content scrolls behind it.
  useEffect(() => {
    if (!notifOpen && !profileOpen) return;
    const onScroll = () => { setOpen(false); setProfileOpen(false); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [notifOpen, profileOpen]);

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  // One-tap flip — writes through the exact same path as the Settings → Appearance buttons
  // (same updateTheme API call, same applyTheme/updateUser calls), so the two never disagree.
  const themeMutation = useMutation({
    mutationFn: (theme) => updateTheme(theme),
    onSuccess:  (_, theme) => { applyTheme(theme); updateUser({ dashboardTheme: theme }); },
  });
  const toggleTheme = () => themeMutation.mutate(currentTheme === 'dark' ? 'light' : 'dark');

  return (
    <nav className="bg-nav border-b border-divider px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm">
      {/* Left */}
      <div className="flex items-center gap-3">
        {onMenuToggle && (
          <button
            onClick={onMenuToggle}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl text-muted hover:bg-nav-hover transition-colors"
          >
            <MdMenu className="text-2xl" />
          </button>
        )}
        <h1 className="font-medium text-secondary text-base">{pageTitle}</h1>
      </div>

      {/* Right */}
      {user && (
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize hidden sm:block ${ROLE_BADGE[user.role]}`}>
            {user.role}
          </span>

          {/* Theme toggle — sun when dark is active (tap for light), moon when light is active (tap for dark) */}
          <button
            onClick={toggleTheme}
            disabled={themeMutation.isPending}
            aria-label={currentTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={currentTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-9 h-9 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors disabled:opacity-50"
          >
            {currentTheme === 'dark' ? <MdLightMode size={20} /> : <MdDarkMode size={20} />}
          </button>

          {/* Notification Bell */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setOpen(o => !o)}
              className="relative w-9 h-9 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors"
            >
              <MdNotifications size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary text-white text-[10px] font-semibold leading-none ring-2 ring-nav">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <>
                {/* Tap-catcher backdrop — dismiss on any outside tap/click (mobile-friendly) */}
                <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                <div className={`absolute right-0 top-12 w-80 max-w-[calc(100vw-2rem)] bg-card rounded-2xl shadow-2xl border border-divider z-50 overflow-hidden origin-top-right transition-all duration-150 ${notifShown ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-divider">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-secondary text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <span className="text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded-full bg-primary-light text-primary">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={() => readAllMutation.mutate()}
                        disabled={readAllMutation.isPending}
                        className="text-xs font-medium text-primary px-2 py-1 rounded-lg hover:bg-nav-hover transition-colors disabled:opacity-50 cursor-pointer"
                      >
                        Mark all read
                      </button>
                    )}
                    <button onClick={() => setOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-lg text-muted hover:text-secondary hover:bg-nav-hover transition-colors cursor-pointer">
                      <MdClose />
                    </button>
                  </div>
                </div>

                {/* List */}
                <div className="max-h-80 overflow-y-auto">
                  {!notifications.length ? (
                    <div className="py-10 px-4 text-center">
                      <MdNotificationsNone className="text-4xl mx-auto mb-2 text-subtle" />
                      <p className="text-sm text-muted">You're all caught up</p>
                    </div>
                  ) : (
                    notifications.map(n => {
                      const meta = notifMeta(n.type);
                      const Icon = meta.Icon;
                      return (
                        <div
                          key={n._id}
                          onClick={() => {
                            readMutation.mutate(n._id);
                            setOpen(false);
                            const target = resolveNotifLink(n.link);
                            if (target) navigate(target); // internal SPA nav only — never a hard reload / catch-all
                          }}
                          className="flex gap-3 px-4 py-3 border-b border-divider last:border-0 cursor-pointer hover:bg-nav-hover transition-colors"
                        >
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.tile}`}>
                            <Icon className="text-base" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm leading-tight text-secondary ${!n.isRead ? 'font-semibold' : 'font-medium'}`}>
                              {n.title}
                            </p>
                            <p className="text-xs text-muted mt-0.5 line-clamp-2">{n.message}</p>
                            <p className="text-[10px] text-subtle mt-1">{fmtRelative(n.createdAt)}</p>
                          </div>
                          {!n.isRead && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              </>
            )}
          </div>

          {/* Profile dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              type="button"
              onClick={() => setProfileOpen(o => !o)}
              title="Account"
              className="flex items-center gap-2 rounded-full px-1.5 py-1 hover:bg-nav-hover transition-colors focus:outline-none"
            >
              <span className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 block">
                {user.profilePhoto ? (
                  <img src={user.profilePhoto} alt={user.name || 'You'} className="w-full h-full object-cover" />
                ) : (
                  <span className="w-full h-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                    {(user.name || '?')[0].toUpperCase()}
                  </span>
                )}
              </span>
              <span className="hidden md:flex flex-col leading-tight text-left">
                <span className="text-sm font-medium text-secondary">{user.name}</span>
                {user.clubName && <span className="text-xs text-muted">{user.clubName}</span>}
              </span>
            </button>

            {profileOpen && (
              <>
                {/* Tap-catcher backdrop — dismiss on any outside tap/click (mobile-friendly) */}
                <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                <div className={`absolute right-0 top-12 w-56 bg-card rounded-2xl shadow-2xl border border-divider z-50 overflow-hidden origin-top-right transition-all duration-150 ${profileShown ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <div className="px-4 py-3 border-b border-divider">
                  <p className="text-sm font-semibold text-secondary truncate">{user.name}</p>
                  {user.clubName && <p className="text-xs text-muted truncate">{user.clubName}</p>}
                </div>
                <button
                  onClick={() => { setProfileOpen(false); navigate(`/${user.role}/settings`); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-secondary hover:bg-nav-hover transition-colors text-left"
                >
                  <MdSettings className="text-lg text-muted" />
                  Profile & Settings
                </button>
                <button
                  onClick={() => { setProfileOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-nav-hover transition-colors text-left border-t border-divider"
                >
                  <MdLogout className="text-lg" />
                  Logout
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
